import React, { cloneElement, isValidElement, useCallback, useContext, useRef, useState, } from 'react';
import { observer } from 'mobx-react-lite';
import isObject from 'lodash/isObject';
import classNames from 'classnames';
import TableHeaderCell from './TableHeaderCell';
import TableVirtualHeaderCell from './TableVirtualHeaderCell';
import TableContext from './TableContext';
import { ColumnLock } from './enum';
import { getEditorByField, getTableHeaderRows, isStickySupport } from './utils';
import TableHeaderRow from './TableHeaderRow';
import ExpandIcon from './ExpandIcon';
const TableHeader = function TableHeader(props) {
    const { lock, queryFields } = props;
    const { prefixCls, border, tableStore, dataSet, fullColumnWidth } = useContext(TableContext);
    const { columnResizable, columnResizing, columnGroups, comboBarStatus, rowHeight, isRenderRange } = tableStore;
    const { columns } = columnGroups;
    const needIntersection = tableStore.isFixedRowHeight && tableStore.virtual && tableStore.overflowX;
    const headerRows = getTableHeaderRows(lock ? columns.filter((group) => group.lock === lock) : columns);
    const [isHeaderHover, setIsHeaderHover] = useState();
    const nodeRef = useRef(null);
    const getHeaderNode = useCallback(() => {
        return nodeRef.current;
    }, [nodeRef]);
    const handleTheadMouseEnter = useCallback(() => {
        setIsHeaderHover(true);
    }, []);
    const handleTheadMouseLeave = useCallback(() => {
        setIsHeaderHover(false);
    }, []);
    const isExpanded = tableStore.isBodyExpanded;
    const handleExpandChange = useCallback(() => {
        tableStore.setBodyExpanded(!isExpanded);
    }, [tableStore, isExpanded]);
    const renderExpandIcon = () => {
        const record = dataSet.get(0);
        if (record) {
            const { expandIcon } = tableStore;
            if (typeof expandIcon === 'function') {
                return expandIcon({
                    prefixCls,
                    expanded: isExpanded,
                    expandable: true,
                    needIndentSpaced: false,
                    onExpand: handleExpandChange,
                    record,
                });
            }
            return (React.createElement(ExpandIcon, { prefixCls: prefixCls, expandable: true, onChange: handleExpandChange, expanded: isExpanded }));
        }
    };
    const expandIconColumnIndex = tableStore.props.bodyExpandable ? (lock === ColumnLock.right ? columnGroups.leafs.filter(group => group.column.lock !== ColumnLock.right).length : 0) : -1;
    const hasExpandIcon = (columnIndex) => (expandIconColumnIndex > -1 && (columnIndex + expandIconColumnIndex) === tableStore.expandIconColumnIndex);
    const getTrs = (isSearchTr) => {
        const notRenderThKey = ['__selection-column__', '__combo-column__'];
        return headerRows.map((row, rowIndex) => {
            const { length } = row;
            const rowKey = String(rowIndex);
            if (length) {
                const notLockLeft = lock !== ColumnLock.left;
                const lastColumnClassName = notLockLeft ? `${prefixCls}-cell-last` : undefined;
                const hasPlaceholder = tableStore.overflowY && rowIndex === 0 && notLockLeft;
                const useEmptyColumn = !fullColumnWidth && rowIndex === 0 && notLockLeft && !tableStore.overflowX && !tableStore.hasEmptyWidthColumn;
                const tds = row.map((col, index) => {
                    if (!col.hidden) {
                        const { key, rowSpan, colSpan, children } = col;
                        const cellProps = {
                            key,
                            columnGroup: col,
                            getHeaderNode,
                            rowIndex,
                            isSearchCell: isSearchTr,
                            scope: children ? 'colgroup' : 'col',
                            isRenderCell: isRenderRange(index, headerRows.length > 1),
                        };
                        if (!useEmptyColumn && notLockLeft && !hasPlaceholder && index === length - 1 && columnGroups.lastLeaf === col.lastLeaf) {
                            cellProps.className = lastColumnClassName;
                        }
                        if (isSearchTr) {
                            cellProps.className = `${cellProps.className} ${prefixCls}-thead-inline-search`;
                        }
                        if (rowSpan > 1 || children) {
                            cellProps.rowSpan = rowSpan;
                        }
                        if (colSpan > 1 || children) {
                            cellProps.colSpan = colSpan;
                        }
                        const icon = !isSearchTr ?
                            (rowIndex === headerRows.length - 1 && hasExpandIcon(index) ? renderExpandIcon() : undefined) :
                            (!notRenderThKey.includes(String(key)) && getQueryFields({ width: '100%' }).find(field => field.key === key));
                        if (needIntersection) {
                            return (React.createElement(TableVirtualHeaderCell, Object.assign({}, cellProps), icon));
                        }
                        return (React.createElement(TableHeaderCell, Object.assign({}, cellProps), icon));
                    }
                    return undefined;
                });
                if (useEmptyColumn) {
                    tds.push(React.createElement("th", { key: "empty-column", className: `${prefixCls}-cell ${lastColumnClassName}`, rowSpan: headerRows.length, style: { lineHeight: 1 } }));
                }
                if (hasPlaceholder) {
                    const placeHolderProps = {
                        key: 'fixed-column',
                        rowSpan: headerRows.length,
                        style: {
                            lineHeight: 1,
                        },
                    };
                    const classList = [`${prefixCls}-cell`, lastColumnClassName];
                    if (isStickySupport() && tableStore.overflowX) {
                        if (tableStore.isAnyColumnsRightLock) {
                            placeHolderProps.style.right = 0;
                        }
                        classList.push(`${prefixCls}-cell-fix-right`);
                    }
                    placeHolderProps.className = classList.join(' ');
                    tds.push(React.createElement("th", Object.assign({}, placeHolderProps), "\u00A0"));
                }
                return (React.createElement(TableHeaderRow, { key: rowKey, rowIndex: rowIndex, rows: headerRows, lock: lock }, tds));
            }
            return React.createElement("tr", { key: rowKey });
        });
    };
    const getQueryFields = (extraStyle) => {
        const { queryDataSet } = dataSet;
        const result = [];
        if (queryDataSet) {
            const { fields } = queryDataSet;
            return [...fields.entries()].reduce((list, [name, field]) => {
                if (!field.get('bind')) {
                    const props = {
                        key: name,
                        name,
                        dataSet: queryDataSet,
                    };
                    const element = queryFields[name];
                    list.push(isValidElement(element)
                        ? cloneElement(element, {
                            placeholder: field.get('label'),
                            ...props,
                            style: { height: rowHeight },
                        })
                        : cloneElement(getEditorByField(field), {
                            placeholder: field.get('label'),
                            ...props,
                            style: {
                                ...props.style || {},
                                ...extraStyle,
                                height: rowHeight,
                            },
                            ...(isObject(element) ? element : {}),
                        }));
                }
                return list;
            }, result);
        }
        return result;
    };
    const theadProps = {
        ref: nodeRef,
        className: classNames(`${prefixCls}-thead`, {
            [`${prefixCls}-column-resizing`]: columnResizing,
            [`${prefixCls}-column-resizable`]: columnResizable,
            [`${prefixCls}-thead-hover`]: isHeaderHover || columnResizing,
        }),
    };
    if (!isStickySupport() && !border && tableStore.overflowX) {
        theadProps.onMouseEnter = handleTheadMouseEnter;
        theadProps.onMouseLeave = handleTheadMouseLeave;
    }
    return (React.createElement("thead", Object.assign({}, theadProps),
        getTrs(),
        comboBarStatus && getTrs(true)));
};
TableHeader.displayName = 'TableHeader';
export default observer(TableHeader);
